import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';
import { formatProfileName } from '@/utils/profileHelpers';
import { ChatAvatar } from '@/components/chat/ChatAvatar';

/** Same bound useVolunteersPage applies to an org's event list. */
const ORG_EVENTS_LIMIT = 200;
const EVENT_VOLUNTEERS_LIMIT = 500;

/** Drag further than this and the sheet dismisses instead of springing back. */
const DISMISS_DRAG_PX = 120;

type EventOption = { id: string; name: string | null; image: string | null };
type VolunteerOption = { id: string; name: string; image: string | null };

type Props = {
  open: boolean;
  organizationId: string;
  onClose: () => void;
  onPick: (eventId: string, volunteerId: string) => void;
};

/**
 * Two-step picker for starting a private thread: choose one of the org's live events,
 * then one of its active volunteers.
 */
export function NewDirectChatModal({
  open,
  organizationId,
  onClose,
  onPick,
}: Props) {
  const [events, setEvents] = useState<EventOption[]>([]);
  const [volunteers, setVolunteers] = useState<VolunteerOption[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  /** Drives the slide-up. Set on the frame after mount so the transition actually runs. */
  const [entered, setEntered] = useState(false);
  /** Live finger offset while dragging the sheet down, in px. Never negative. */
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartYRef = useRef(0);
  /** Pointer capture is deferred until the gesture is clearly a drag, so taps still click. */
  const dragCapturedRef = useRef(false);
  // Desktop renders a centred dialog with transform:none, so dragging must not apply there.
  const canDrag = useBreakpointValue({ base: true, md: false }) ?? true;

  useEffect(() => {
    if (!open) {
      setSelectedEvent(null);
      setVolunteers([]);
      setQuery('');
      setError(null);
      setEntered(false);
      setDragY(0);
      setDragging(false);
      return;
    }
    // Start off-screen, then animate in on the next frame — setting both in the same
    // frame would paint the final position with no transition.
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  const loadEvents = useCallback(async () => {
    if (!open || !organizationId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('events')
        .select('id, name, image, end_date, is_active')
        .eq('created_by', organizationId)
        .order('created_at', { ascending: false })
        .limit(ORG_EVENTS_LIMIT);
      if (err) throw err;

      const now = Date.now();
      // Only live events: a thread on an ended event would be hidden the moment it opened.
      const live = (
        (data ?? []) as {
          id: string;
          name: string | null;
          image: string | null;
          end_date: string | null;
          is_active: boolean | null;
        }[]
      )
        .filter(
          (e) =>
            e.is_active !== false &&
            (e.end_date == null || new Date(e.end_date).getTime() >= now)
        )
        .map((e) => ({ id: e.id, name: e.name, image: e.image }));
      setEvents(live);
    } catch (e) {
      logger.error('NewDirectChatModal.loadEvents', e);
      setError(e instanceof Error ? e.message : 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [open, organizationId]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const pickEvent = useCallback(async (ev: EventOption) => {
    setSelectedEvent(ev);
    setQuery('');
    setLoading(true);
    setError(null);
    try {
      const { data: volRows, error: volErr } = await supabase
        .from('volunteers')
        .select('volunteer_id')
        .eq('event_id', ev.id)
        .eq('is_active', true)
        .limit(EVENT_VOLUNTEERS_LIMIT);
      if (volErr) throw volErr;

      const ids = [
        ...new Set(
          ((volRows ?? []) as { volunteer_id: string | null }[])
            .map((r) => r.volunteer_id)
            .filter((id): id is string => Boolean(id))
        ),
      ];
      if (ids.length === 0) {
        setVolunteers([]);
        return;
      }

      const { data: profRows, error: profErr } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image')
        .in('id', ids);
      if (profErr) throw profErr;

      setVolunteers(
        (
          (profRows ?? []) as {
            id: string;
            first_name: string | null;
            last_name: string | null;
            profile_image: string | null;
          }[]
        )
          .map((p) => ({
            id: p.id,
            name: formatProfileName(p.first_name, p.last_name),
            image: p.profile_image,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (e) {
      logger.error('NewDirectChatModal.pickEvent', e);
      setError(e instanceof Error ? e.message : 'Failed to load volunteers');
      setVolunteers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Drag-to-dismiss. Bound to the handle/header strip rather than the whole sheet so it
  // can never fight the scrollable list below it for the same gesture.
  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!canDrag) return;
    // The header holds the back and close buttons. Starting a drag on them would swallow
    // their tap, so leave those to behave as ordinary buttons.
    if ((e.target as HTMLElement).closest('button, input')) return;
    dragStartYRef.current = e.clientY;
    dragCapturedRef.current = false;
    setDragging(true);
  };

  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    // Downward only: dragging up shouldn't lift the sheet off the top of the screen.
    const dy = Math.max(0, e.clientY - dragStartYRef.current);
    // Capture only once it is unambiguously a drag; capturing on pointerdown would stop
    // a plain tap in the header from ever becoming a click.
    if (!dragCapturedRef.current && dy > 4) {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragCapturedRef.current = true;
    }
    setDragY(dy);
  };

  const onDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    if (
      dragCapturedRef.current &&
      e.currentTarget.hasPointerCapture(e.pointerId)
    ) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragCapturedRef.current = false;
    if (dragY > DISMISS_DRAG_PX) {
      onClose();
      return;
    }
    setDragY(0); // spring back
  };

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const shownEvents = q
    ? events.filter((e) => (e.name ?? '').toLowerCase().includes(q))
    : events;
  const shownVolunteers = q
    ? volunteers.filter((v) => v.name.toLowerCase().includes(q))
    : volunteers;

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={1400}
      display="flex"
      alignItems={{ base: 'flex-end', md: 'center' }}
      justifyContent="center"
      // Backdrop lightens as the sheet is pulled away, so the drag feels connected.
      opacity={entered ? Math.max(0, 1 - dragY / 400) : 0}
      transition={dragging ? 'none' : 'opacity 0.25s ease-out'}
      onClick={onClose}
    >
      <Box
        bg="white"
        w={{ base: '100%', md: '440px' }}
        // A fixed height on mobile, not maxH: with only a couple of events the sheet
        // used to hug its content and sit low on the screen. Now it always rises to
        // near-full height, so the list has room and the reach is consistent.
        h={{ base: '88vh', md: 'auto' }}
        maxH={{ base: '88vh', md: '70vh' }}
        borderTopRadius="2xl"
        borderRadius={{ md: '2xl' }}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        // Fixed element, so it must consume its own bottom safe-area inset.
        pb={{ base: 'env(safe-area-inset-bottom, 0px)', md: 0 }}
        transform={{
          base: entered ? `translateY(${dragY}px)` : 'translateY(100%)',
          md: 'none',
        }}
        // No transition while the finger is down, or the sheet lags behind the drag.
        transition={dragging ? 'none' : 'transform 0.3s ease-in-out'}
        boxShadow="0 -8px 30px rgba(0,0,0,0.18)"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag region: the handle AND the whole header. Everything from the search input
            down is left alone so the list can scroll normally. touchAction none stops the
            browser claiming the vertical gesture before React sees it. */}
        <Box
          flexShrink={0}
          css={{ touchAction: canDrag ? 'none' : 'auto' }}
          cursor={{ base: 'grab', md: 'default' }}
          _active={{ cursor: { base: 'grabbing', md: 'default' } }}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
          <Flex
            justify="center"
            align="center"
            h="28px"
            display={{ base: 'flex', md: 'none' }}
          >
            <Box
              w="36px"
              h="5px"
              borderRadius="full"
              bg={dragging ? 'volyx.darkGray' : 'volyx.mediumGray'}
              transition="background 0.15s"
            />
          </Flex>
          <Flex
            align="center"
            gap={2}
            p={4}
            borderBottomWidth={1}
            borderColor="gray.200"
          >
            {selectedEvent ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                p={1}
                aria-label="Back to events"
                onClick={() => {
                  setSelectedEvent(null);
                  setQuery('');
                }}
              >
                <ChevronLeft size={20} />
              </Button>
            ) : null}
            <Text fontWeight="semibold" flex={1} lineClamp={1}>
              {selectedEvent ? selectedEvent.name ?? 'Event' : 'New message'}
            </Text>
            <Button
              type="button"
              variant="ghost"
              // Matches the + that opens this sheet, and clears the 44px touch minimum.
              minW="44px"
              h="44px"
              p={0}
              borderRadius="full"
              fontSize="2xl"
              lineHeight="1"
              color="volyx.textSecondary"
              flexShrink={0}
              onClick={onClose}
              aria-label="Close"
              _hover={{ bg: 'volyx.lightGray', color: 'volyx.primary' }}
              _active={{ transform: 'scale(0.92)' }}
              transition="transform 0.12s ease-out, background 0.15s, color 0.15s"
            >
              ✕
            </Button>
          </Flex>
        </Box>

        {/* From here down is NOT draggable: the search input and the scrolling list. */}
        <Box p={3}>
          <Input
            id="new-direct-chat-search"
            name="new-direct-chat-search"
            size="sm"
            borderRadius="lg"
            bg="white"
            placeholder={
              selectedEvent ? 'Search volunteers…' : 'Search events…'
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>

        <Box flex={1} overflowY="auto" minH={0}>
          {loading ? (
            <Text p={4} fontSize="sm" color="gray.500">
              Loading…
            </Text>
          ) : error ? (
            <Text p={4} fontSize="sm" color="red.500">
              {error}
            </Text>
          ) : selectedEvent ? (
            shownVolunteers.length === 0 ? (
              <Text p={4} fontSize="sm" color="gray.500">
                No volunteers on this event yet.
              </Text>
            ) : (
              shownVolunteers.map((v) => (
                <Flex
                  key={v.id}
                  role="button"
                  tabIndex={0}
                  align="center"
                  gap={3}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => onPick(selectedEvent.id, v.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPick(selectedEvent.id, v.id);
                    }
                  }}
                >
                  <ChatAvatar
                    kind="direct"
                    stored={v.image}
                    name={v.name}
                    size="sm"
                  />
                  <Text fontSize="sm" lineClamp={1}>
                    {v.name}
                  </Text>
                </Flex>
              ))
            )
          ) : shownEvents.length === 0 ? (
            <Text p={4} fontSize="sm" color="gray.500">
              No live events to message about.
            </Text>
          ) : (
            shownEvents.map((ev) => (
              <Flex
                key={ev.id}
                role="button"
                tabIndex={0}
                align="center"
                gap={3}
                p={3}
                cursor="pointer"
                _hover={{ bg: 'gray.100' }}
                onClick={() => void pickEvent(ev)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void pickEvent(ev);
                  }
                }}
              >
                <ChatAvatar
                  kind="event"
                  stored={ev.image}
                  name={ev.name}
                  size="sm"
                />
                <Text fontSize="sm" lineClamp={1}>
                  {ev.name ?? 'Unnamed event'}
                </Text>
              </Flex>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
}
